'use client';
import Link from 'next/link';
import { ArrowRight, Clock, ChevronRight, BookOpen, FileText } from 'lucide-react';

const FALLBACK_BLOG    = '/images/blog-cover-2.jpg';
const FALLBACK_ARTICLE = '/images/article-cover-1.jpg';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
}

function PostCard({ post, href, label, badgeClass, fallbackImg }) {
  if (!post) {
    return (
      <Link href={href}
        className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-slate-100">
        <div className="h-56 overflow-hidden relative">
          <img src={fallbackImg} alt={label}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
          <div className="absolute bottom-4 left-4">
            <span className={`text-white text-xs font-bold px-3 py-1 rounded-full ${badgeClass}`}>{label}</span>
          </div>
        </div>
        <div className="p-6">
          <h3 className="font-display font-bold text-slate-900 text-xl mb-2 group-hover:text-sky-600 transition-colors">
            {label === 'Blog' ? 'Travel Stories & Tips' : 'In-Depth Travel Articles'}
          </h3>
          <p className="text-slate-500 text-sm">
            {label === 'Blog'
              ? 'Expert tips, destination guides, and insider tricks for every kind of traveler.'
              : 'In-depth insights on aviation, travel hacks, packing guides, and destination breakdowns.'}
          </p>
          <span className="flex items-center gap-1 text-sky-500 font-semibold text-sm mt-4 group-hover:gap-2 transition-all">
            Read {label} <ChevronRight size={16} />
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link href={`${href}/${post.slug}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100">
      <div className="h-56 overflow-hidden relative bg-slate-100">
        <img
          src={post.coverImage || fallbackImg}
          alt={post.coverImageAlt || post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          <span className={`text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 ${badgeClass}`}>
            {label === 'Blog' ? <BookOpen size={11} /> : <FileText size={11} />} {label}
          </span>
          {post.category && (
            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full">
              {post.category}
            </span>
          )}
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
          {post.publishedAt && (
            <span className="flex items-center gap-1"><Clock size={11} /> {formatDate(post.publishedAt)}</span>
          )}
          {post.readTime && <span>· {post.readTime} min read</span>}
        </div>
        <h3 className="font-display font-bold text-slate-900 text-xl mb-2 group-hover:text-sky-600 transition-colors line-clamp-2">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-slate-500 text-sm leading-relaxed line-clamp-2">{post.excerpt}</p>
        )}
        <span className="flex items-center gap-1 text-sky-500 font-semibold text-sm mt-4 group-hover:gap-2 transition-all">
          Read {label} <ChevronRight size={16} />
        </span>
      </div>
    </Link>
  );
}

export default function BlogArticlesPreview({ latestBlog = null, latestArticle = null }) {
  return (
    <section className="py-20 px-6 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-sky-500 font-semibold text-sm tracking-widest uppercase mb-2">
              Latest Updates
            </p>

            <h2 className="font-display text-4xl font-bold text-slate-900 mb-2">
              Blogs & Articles
            </h2>
            <p>Find out what’s new and trending in the travel world!</p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <Link
              href="/blog"
              className="flex items-center gap-1 text-sm font-semibold text-sky-500 hover:text-sky-700 transition-colors"
            >
              Blog <ArrowRight size={16} />
            </Link>

            <Link
              href="/articles"
              className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
            >
              Articles <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <PostCard post={latestBlog}    href="/blog"     label="Blog"    badgeClass="bg-sky-500"   fallbackImg={FALLBACK_BLOG} />
          <PostCard post={latestArticle} href="/articles" label="Article" badgeClass="bg-slate-700" fallbackImg={FALLBACK_ARTICLE} />
        </div>

        <div className="flex justify-center gap-6 mt-8 md:hidden">
          <Link href="/blog"     className="text-sky-500 font-semibold text-sm flex items-center gap-1">All Blogs <ArrowRight size={14} /></Link>
          <Link href="/articles" className="text-slate-600 font-semibold text-sm flex items-center gap-1">All Articles <ArrowRight size={14} /></Link>
        </div>
      </div>
    </section>
  );
}
